import { MindMapNode, MindMapConfig, ParseResult } from '../types';

const CONFIG_BLOCK_START = "<!-- MINDMAP_CONFIG";
const CONFIG_BLOCK_END = "-->";

// Helper to generate a stable ID based on path
const generateId = (path: number[]): string => {
  if (path.length === 0) return 'root';
  return path.join('-');
};

export const parseMarkdownToTree = (markdown: string): ParseResult => {
  const parts = markdown.split(CONFIG_BLOCK_START);
  const content = parts[0];
  let config: MindMapConfig = { themeName: 'Midnight', meta: {} };

  // Parse Metadata if exists
  if (parts.length > 1) {
    try {
      const jsonStr = parts[1].split(CONFIG_BLOCK_END)[0];
      const parsed = JSON.parse(jsonStr.trim());
      config = { ...config, ...parsed };
    } catch (e) {
      console.warn("Failed to parse mindmap config", e);
    }
  }

  const lines = content.split('\n');
  const root: MindMapNode = { id: 'root', name: 'Root', children: [] };
  
  // Stack: Maintains the path to the current parent.
  const stack: { node: MindMapNode; level: number; path: number[] }[] = [
    { node: root, level: -1, path: [] }
  ];

  let lastHeaderLevel = -1; 

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    let level = 0;
    let text = '';
    let isList = false;

    if (trimmed.startsWith('#')) {
      const match = trimmed.match(/^(#+)\s+(.*)/);
      if (match) {
        level = match[1].length - 1; 
        text = match[2];
        lastHeaderLevel = level;
      }
    } else if (trimmed.startsWith('-') || trimmed.startsWith('*')) {
      isList = true;
      const indentMatch = line.match(/^(\s*)/);
      const indent = indentMatch ? indentMatch[1].length : 0;
      
      const indentLevel = Math.floor(indent / 2);
      level = lastHeaderLevel + 1 + indentLevel;
      
      text = trimmed.replace(/^[-*]\s+/, '');
      text = text.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
    } else {
        return;
    }
    
    if (level < 0) level = 0;

    while (stack.length > 1 && stack[stack.length - 1].level >= level) {
      stack.pop();
    }

    const parentCtx = stack[stack.length - 1];
    const parent = parentCtx.node;
    
    // Generate ID
    const childIndex = parent.children ? parent.children.length : 0;
    const currentPath = [...parentCtx.path, childIndex];
    const id = generateId(currentPath);
    
    // Apply Metadata
    const meta = config.meta[id] || {};
    
    const newNode: MindMapNode = {
      id,
      name: text,
      children: [],
      isCollapsed: meta.collapsed,
      isFloating: meta.isFloating,
      offset: (meta.x !== undefined && meta.y !== undefined) ? { x: meta.x, y: meta.y } : undefined,
      quadrant: meta.quadrant,
      description: meta.description
    };

    if (!parent.children) parent.children = [];
    parent.children.push(newNode);
    
    stack.push({ node: newNode, level, path: currentPath });
  });

  // Post-Processing Root
  let finalRoot: MindMapNode;
  
  if (root.children && root.children.length === 1) {
    finalRoot = root.children[0];
    finalRoot.id = 'root';
    const rootMeta = config.meta['root'];
    if (rootMeta) {
        finalRoot.offset = (rootMeta.x && rootMeta.y) ? { x: rootMeta.x, y: rootMeta.y } : undefined;
        finalRoot.isCollapsed = rootMeta.collapsed;
        finalRoot.isFloating = rootMeta.isFloating;
        finalRoot.description = rootMeta.description;
    }
  } else if (root.children && root.children.length > 0) {
    root.name = "Mind Map";
    finalRoot = root;
  } else {
    finalRoot = { id: 'root', name: 'Start Typing...', children: [] };
  }

  return { root: finalRoot, config };
};

export const convertTreeToMarkdown = (root: MindMapNode, themeName: string, view?: {x: number, y: number, k: number}, history?: MindMapNode[]): string => {
  let md = "";
  const meta: { [id: string]: any } = {};

  const traverse = (node: MindMapNode, depth: number) => {
    // Store metadata
    if (node.offset || node.isCollapsed || node.isFloating || node.quadrant || node.description) {
        meta[node.id] = {};
        if (node.offset) {
            meta[node.id].x = Math.round(node.offset.x);
            meta[node.id].y = Math.round(node.offset.y);
        }
        if (node.isCollapsed) meta[node.id].collapsed = true;
        if (node.isFloating) meta[node.id].isFloating = true;
        if (node.quadrant) meta[node.id].quadrant = node.quadrant;
        if (node.description) meta[node.id].description = node.description;
    }

    const indent = "  ".repeat(Math.max(0, depth - 1));
    
    if (depth === 0) {
        if (node.name !== "Mind Map" && node.name !== "Root") {
             md += `# ${node.name}\n`;
        }
    } else if (depth === 1) {
        md += `## ${node.name}\n`;
    } else {
        md += `${indent}- ${node.name}\n`;
    }

    if (node.children) {
        node.children.forEach(child => traverse(child, depth + 1));
    }
  };

  traverse(root, 0);

  // Clean history to reduce file size (remove empty arrays and falsey default flags)
  const cleanHistory = history ? history.map(h => {
    return JSON.parse(JSON.stringify(h, (k, v) => {
       if (k === 'children' && Array.isArray(v) && v.length === 0) return undefined;
       if (k === 'isCollapsed' && !v) return undefined;
       if (k === 'isFloating' && !v) return undefined;
       return v;
    }));
  }) : undefined;

  const config: MindMapConfig = { themeName, view, history: cleanHistory, meta };
  md += `\n\n${CONFIG_BLOCK_START}\n${JSON.stringify(config)}\n${CONFIG_BLOCK_END}`;
  
  return md;
};